import React from 'react';
import { Checkbox, Col, Form, Row } from 'antd';
import type { FormInstance } from 'antd';
import {
  CustomDynamicSelect,
  CustomInput,
  CustomParagraph,
} from '../../../components';
import PhoneInput from 'react-phone-input-2';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { prefferedLanguages } from '../../../common/static_data';
import PreferredChannelSelector from './PreferredChannelSelector';
import { ContactFormData, Contact } from './types';

type PreferredChannel = 'phone' | 'email' | 'sms' | 'none';

type Props = {
  form: FormInstance<ContactFormData>;
  contactList: Contact[];
  preferredChannel: PreferredChannel | null;
  setPreferredChannel: (channel: PreferredChannel | null) => void;
};

const DirectContactForm: React.FC<Props> = ({
  form,
  contactList,
  preferredChannel,
  setPreferredChannel,
}) => {
  return (
    <Form
      form={form}
      name='AddNewContact'
      layout='vertical'
      autoComplete='off'
      initialValues={{ preferred_language: 'English' }}
    >
      <Row gutter={[0, 16]}>
        <Col span={24}>
          <Form.Item name='country_code' hidden>
            <input type='hidden' />
          </Form.Item>
          <Form.Item name='country_region' hidden>
            <input type='hidden' />
          </Form.Item>

          <Form.Item
            className='custom-input form-input-style'
            label='Name'
            name='name'
            rules={[
              { required: true, message: 'Please enter name' },
              {
                pattern: /^[A-Za-z\s'-]+$/,
                message: 'Name must contain only letters',
              },
            ]}
          >
            <CustomInput
              placeholder='Enter name'
              height='36px'
              width='100%'
              border='0px'
              bgColor='var(--light-gray)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style'
            label='Title'
            name='title'
            rules={[{ required: true, message: 'Please enter title' }]}
          >
            <CustomInput
              placeholder='Enter title'
              height='36px'
              width='100%'
              border='0px'
              bgColor='var(--light-gray)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
            />
          </Form.Item>
        </Col>
        <Col span={24} style={{ display: 'none' }}>
          <Form.Item
            className='custom-input form-input-style'
            name='country_code'
          >
            <CustomInput
              placeholder='Country code'
              height='36px'
              width='100%'
              border='0px'
              bgColor='var(--light-gray)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style'
            label='Email'
            name='email'
            dependencies={['phone_number']}
            rules={[
              { type: 'email', message: 'Please enter a valid email' },
              {
                validator: (_, value) => {
                  const phone = form.getFieldValue('phone_number');
                  if (!value && !phone) {
                    return Promise.reject(
                      'Please provide either Email or Phone'
                    );
                  }
                  if (!value) return Promise.resolve();

                  const currentId = form.getFieldValue('id');
                  const exists = contactList.some(
                    (c) =>
                      c.email?.toLowerCase() === value.toLowerCase() &&
                      c.id !== currentId
                  );
                  return exists
                    ? Promise.reject('This email is already in use')
                    : Promise.resolve();
                },
              },
            ]}
          >
            <CustomInput
              placeholder='Enter email'
              height='36px'
              width='100%'
              border='0px'
              bgColor='var(--light-gray)'
              borderRadius='8px'
              placeholderColor='var(--text-secondary)'
              lineHeight='38px'
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <div>
            <Form.Item
              label='Phone number'
              name='phone_number'
              style={{ marginBottom: '0px' }}
              dependencies={['email']}
              rules={[
                {
                  validator: (_, value) => {
                    const email = form.getFieldValue('email');
                    if (!value && !email) {
                      return Promise.reject(
                        'Please provide either Email or Phone'
                      );
                    }
                    if (value && !isValidPhoneNumber(value)) {
                      return Promise.reject('Invalid phone number');
                    }
                    if (!value) return Promise.resolve();

                    const currentId = form.getFieldValue('id');
                    const exists = contactList.some(
                      (c) =>
                        (c.api?.phone_number ?? c.phone) === value &&
                        c.id !== currentId
                    );
                    return exists
                      ? Promise.reject('This phone number is already in use')
                      : Promise.resolve();
                  },
                },
              ]}
            >
              <PhoneInput
                country={'us'}
                inputStyle={{
                  height: 36,
                  width: '100%',
                  border: 'none',
                  background: 'transparent',
                  paddingLeft: 48,
                }}
                buttonStyle={{
                  background: 'transparent',
                  borderTopLeftRadius: 8,
                  borderBottomLeftRadius: 8,
                  border: 'none',
                }}
                onChange={(value, data, _event, formattedValue) => {
                  const countryData = data as {
                    dialCode?: string;
                    countryCode?: string;
                  };
                  form.setFieldsValue({
                    phone_number: formattedValue || value,
                    country_code: countryData?.dialCode
                      ? `+${countryData.dialCode}`
                      : '',
                    country_region: countryData?.countryCode
                      ? String(countryData.countryCode).toUpperCase()
                      : '',
                  });
                }}
              />
            </Form.Item>
          </div>
        </Col>

        <Col span={24}>
          <Form.Item
            className='custom-input form-input-style font-black-color'
            label='Preferred language'
            name='preferred_language'
            rules={[{ required: false, message: 'Please select value' }]}
            style={{ marginBottom: '0px' }}
          >
            <CustomDynamicSelect
              placeholder='Select'
              height='36px'
              border='0px'
              bgColor='var(--light-gray)'
              textColor='var(--text-secondary)'
              borderRadius='8px'
              lineHeight='16px'
              options={prefferedLanguages}
            />
          </Form.Item>
          <CustomParagraph
            text='AI-generated communications sent to contact will be sent in this language'
            fontSize='12px'
            fontWeight='400'
            lineHeight='20px'
            marginBottom='0'
            marginTop='8px'
            color='var(--text-secondary)'
          />
        </Col>

        <PreferredChannelSelector
          value={preferredChannel ?? 'phone'}
          onChange={setPreferredChannel}
          isDirect={true}
        />
        <Form.Item label='' name='is_primary_contact' valuePropName='checked'>
          <Checkbox>Make primary contact</Checkbox>
        </Form.Item>
      </Row>
    </Form>
  );
};

export default DirectContactForm;
